import { useEffect, useState } from 'react';
import { fetchNbsRates } from '../lib/nbs';
import { CURRENCIES } from '../lib/helpers';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';

// ============================================================
// KURSNA LISTA — srednji kurs NBS za izabrani dan
// ============================================================

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayIso = () => isoOf(new Date());
const dmy = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}.` : '');
const shift = (iso, days) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return isoOf(d); };
const MIN_DATE = '2002-05-15';

// Valute za kalkulator (iste kao na inostranoj fakturi) + dinar
const CONV_CURRENCIES = [
  ['RSD', 'Srpski dinar', 'Serbian dinar', 'Сербский динар'],
  ['EUR', 'Evro', 'Euro', 'Евро'],
  ['USD', 'Američki dolar', 'US dollar', 'Доллар США'],
  ['CHF', 'Švajcarski franak', 'Swiss franc', 'Швейцарский франк'],
  ['GBP', 'Britanska funta', 'British pound', 'Фунт стерлингов'],
  ['BAM', 'Konvertibilna marka (BiH)', 'Convertible mark (BiH)', 'Конвертируемая марка (БиГ)'],
  ['MKD', 'Makedonski denar', 'Macedonian denar', 'Македонский денар'],
  ['HUF', 'Mađarska forinta', 'Hungarian forint', 'Венгерский форинт'],
  ['CZK', 'Češka kruna', 'Czech koruna', 'Чешская крона'],
  ['PLN', 'Poljski zlot', 'Polish zloty', 'Польский злотый'],
  ['SEK', 'Švedska kruna', 'Swedish krona', 'Шведская крона'],
  ['NOK', 'Norveška kruna', 'Norwegian krone', 'Норвежская крона'],
  ['DKK', 'Danska kruna', 'Danish krone', 'Датская крона'],
  ['CAD', 'Kanadski dolar', 'Canadian dollar', 'Канадский доллар'],
  ['AUD', 'Australijski dolar', 'Australian dollar', 'Австралийский доллар'],
  ['JPY', 'Japanski jen', 'Japanese yen', 'Японская иена'],
  ['CNY', 'Kineski juan', 'Chinese yuan', 'Китайский юань'],
  ['RUB', 'Ruska rublja', 'Russian ruble', 'Российский рубль'],
];
const FETCH_CURRENCIES = [...new Set([...CURRENCIES, ...CONV_CURRENCIES.map(([c]) => c).filter((c) => c !== 'RSD')])];

// "1.234,56" ili "1234.56" → 1234.56
const parseAmount = (v) => {
  let s = String(v || '').trim().replace(/\s/g, '');
  if (!s) return NaN;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, ''); // "1.000" = hiljadu
  return Number(s);
};
const fmt2 = (n) => n.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n) => n.toLocaleString('sr-RS', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function ExchangeList() {
  const { t, tr } = useI18n();
  const [date, setDate] = useState(todayIso());
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Kalkulator
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState('EUR');
  const [to, setTo] = useState('RSD');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      setLoading(true);
      try {
        const r = await fetchNbsRates(FETCH_CURRENCIES, date === todayIso() ? undefined : date);
        if (cancelled) return;
        setRates(r);
        if (Object.values(r).every((x) => !x)) setError(t('exchangeError'));
      } catch (e) {
        if (!cancelled) { setRates(null); setError(e.message || t('exchangeError')); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const isToday = date === todayIso();
  const rateDates = rates ? [...new Set(Object.values(rates).filter(Boolean).map((r) => r.date))] : [];
  const differentDay = rateDates.length > 0 && !rateDates.includes(date);

  // Preračun: iznos × kurs(iz) ÷ kurs(u); dinar ima kurs 1
  const rateOf = (c) => (c === 'RSD' ? 1 : rates?.[c]?.rate || null);
  const amt = parseAmount(amount);
  const rFrom = rateOf(from);
  const rTo = rateOf(to);
  const canCalc = !loading && rFrom && rTo && Number.isFinite(amt) && amt >= 0 && amount !== '';
  const result = canCalc ? (amt * rFrom) / rTo : null;
  const unitRate = rFrom && rTo ? rFrom / rTo : null;
  const usedDate = rates?.[from !== 'RSD' ? from : to]?.date || date;
  const curName = (c) => {
    const row = CONV_CURRENCIES.find(([code]) => code === c);
    return row ? tr(row[1], row[2], row[3]) : c;
  };
  const curOptions = CONV_CURRENCIES.map(([code]) => (
    <option key={code} value={code}>{code} — {curName(code)}</option>
  ));

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <h1 style={styles.heading}>{t('exchangeHeading')}</h1>
      <p style={styles.sub}>{t('exchangeSub')}</p>

      <div style={styles.picker}>
        <button onClick={() => setDate((d) => shift(d, -1))} disabled={date <= MIN_DATE || loading} style={styles.navBtn}
          aria-label={tr('Prethodni dan', 'Previous day', 'Предыдущий день')} title={tr('Prethodni dan', 'Previous day', 'Предыдущий день')}>
          <Icon name="arrowLeft" size={16} />
        </button>
        <label style={styles.dateWrap}>
          <span style={styles.dateLabel}>{tr('Kurs na dan', 'Rate on', 'Курс на дату')}</span>
          <input type="date" value={date} min={MIN_DATE} max={todayIso()}
            onChange={(e) => e.target.value && setDate(e.target.value > todayIso() ? todayIso() : e.target.value)}
            style={styles.dateInput} />
        </label>
        <button onClick={() => setDate((d) => shift(d, 1))} disabled={isToday || loading} style={styles.navBtn}
          aria-label={tr('Sledeći dan', 'Next day', 'Следующий день')} title={tr('Sledeći dan', 'Next day', 'Следующий день')}>
          <Icon name="arrowLeft" size={16} style={{ transform: 'rotate(180deg)' }} />
        </button>
        {!isToday && (
          <button onClick={() => setDate(todayIso())} style={styles.todayBtn}>{tr('Danas', 'Today', 'Сегодня')}</button>
        )}
      </div>

      {/* Kalkulator: iznos iz jedne valute u drugu po srednjem kursu NBS za izabrani dan */}
      <div style={styles.calc} className="fx-calc">
        <div style={styles.calcTitle}>
          <Icon name="exchange" size={18} color="var(--teal-800)" />
          {tr('Preračun po kursu NBS', 'Convert at the NBS rate', 'Пересчёт по курсу НБС')}
        </div>
        <div style={styles.calcGrid} className="fx-calc-grid">
          <label style={styles.calcField} className="fx-amount">
            <span style={styles.dateLabel}>{tr('Iznos', 'Amount', 'Сумма')}</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,\s]/g, ''))}
              inputMode="decimal"
              placeholder="1.000,00"
              style={styles.calcInput}
              aria-label={tr('Iznos za preračun', 'Amount to convert', 'Сумма для пересчёта')}
            />
          </label>
          <label style={styles.calcField} className="fx-from">
            <span style={styles.dateLabel}>{tr('Iz valute', 'From', 'Из валюты')}</span>
            <select value={from} onChange={(e) => setFrom(e.target.value)} style={styles.calcInput}>{curOptions}</select>
          </label>
          <button
            onClick={() => { setFrom(to); setTo(from); }}
            style={styles.swapBtn}
            className="fx-swap"
            aria-label={tr('Zameni smer', 'Swap direction', 'Поменять направление')}
            title={tr('Zameni smer', 'Swap direction', 'Поменять направление')}
          >
            <Icon name="exchange" size={18} />
          </button>
          <label style={styles.calcField} className="fx-to">
            <span style={styles.dateLabel}>{tr('U valutu', 'To', 'В валюту')}</span>
            <select value={to} onChange={(e) => setTo(e.target.value)} style={styles.calcInput}>{curOptions}</select>
          </label>
        </div>

        <div style={styles.result} aria-live="polite">
          {loading ? '…' : result !== null ? (
            <>
              <div style={styles.resultMain}>
                {fmt2(amt)} {from} = <strong style={{ color: 'var(--teal-800)' }}>{fmt2(result)} {to}</strong>
              </div>
              <div style={styles.resultSub}>
                1 {from} = {fmt4(unitRate)} {to} · {tr('srednji kurs NBS na dan', 'NBS middle rate on', 'средний курс НБС на')} {dmy(usedDate)}
              </div>
            </>
          ) : !rFrom || !rTo ? (
            <div style={styles.resultSub}>{tr('Kurs za izabranu valutu nije dostupan za ovaj dan.', 'The rate for this currency is not available for this day.', 'Курс этой валюты на эту дату недоступен.')}</div>
          ) : (
            <div style={styles.resultSub}>{tr('Upišite iznos i rezultat će se odmah prikazati.', 'Enter an amount and the result will appear instantly.', 'Введите сумму, и результат появится сразу.')}</div>
          )}
        </div>
      </div>

      {differentDay && !loading && (
        <div style={styles.info}>
          {tr(
            `Za ${dmy(date)} NBS nije objavila kurs (vikend ili praznik), pa je prikazan kurs od ${dmy(rateDates[0])}.`,
            `NBS did not publish a rate for ${dmy(date)} (weekend or holiday), so the rate from ${dmy(rateDates[0])} is shown.`,
            `На ${dmy(date)} НБС не публиковал курс (выходной или праздник), поэтому показан курс от ${dmy(rateDates[0])}.`,
          )}
        </div>
      )}
      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.table}>
        <div style={styles.headRow}>
          <span style={styles.colCur}>{tr('Valuta', 'Currency', 'Валюта')}</span>
          <span style={styles.colRate}>{tr('Srednji kurs (RSD)', 'Middle rate (RSD)', 'Средний курс (RSD)')}</span>
          <span style={styles.colDate}>{tr('Datum kursa', 'Rate date', 'Дата курса')}</span>
        </div>
        {CURRENCIES.map((cur) => {
          const r = rates?.[cur];
          return (
            <div key={cur} style={styles.row}>
              <span style={styles.colCur}><strong>{cur}</strong></span>
              <span style={{ ...styles.colRate, ...styles.rate }}>
                {loading ? '…' : r ? r.rate.toLocaleString('sr-RS', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : <span style={styles.na}>{t('notAvailable')}</span>}
              </span>
              <span style={styles.colDate}>{!loading && r ? dmy(r.date) : ''}</span>
            </div>
          );
        })}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .fx-calc-grid { grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) !important; }
          .fx-amount { grid-column: 1 / -1; }
          .fx-calc input, .fx-calc select { font-size: 16px !important; }
        }
      `}</style>
      <div style={styles.source}>{tr('Izvor: Narodna banka Srbije (srednji kurs)', 'Source: National Bank of Serbia (middle rate)', 'Источник: Народный банк Сербии (средний курс)')}</div>
    </div>
  );
}

const styles = {
  heading: { fontSize: 22, marginBottom: 6 },
  sub: { fontSize: 14, color: 'var(--ink-500)', margin: '0 0 16px' },
  picker: { display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  navBtn: { width: 44, height: 44, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--teal-800)' },
  dateWrap: { display: 'flex', flexDirection: 'column', gap: 4 },
  dateLabel: { fontSize: 12, fontWeight: 700, color: 'var(--ink-700)' },
  dateInput: { height: 44, border: '1.5px solid var(--line)', borderRadius: 10, padding: '0 12px', fontSize: 15, background: 'var(--surface)' },
  todayBtn: { height: 44, border: '1px solid var(--teal-800)', background: 'var(--surface)', color: 'var(--teal-800)', borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 13 },
  info: { background: 'var(--gold-100)', color: 'var(--gold-600)', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12 },
  table: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' },
  headRow: { display: 'flex', alignItems: 'center', padding: '12px 16px', background: 'var(--paper)', borderBottom: '1px solid var(--line)', fontSize: 12, fontWeight: 800, color: 'var(--ink-700)' },
  row: { display: 'flex', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid var(--line)', fontSize: 14, color: 'var(--ink-900)' },
  colCur: { width: 90, color: 'var(--teal-800)' },
  colRate: { flex: 1, textAlign: 'right' },
  colDate: { width: 120, textAlign: 'right', fontSize: 12.5, color: 'var(--ink-500)' },
  rate: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  na: { fontWeight: 400, color: 'var(--ink-300)' },
  calc: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: 18, marginBottom: 16 },
  calcTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)', marginBottom: 12 },
  calcGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr) auto minmax(0, 1fr)', gap: 10, alignItems: 'end' },
  calcField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  calcInput: { height: 44, border: '1.5px solid var(--line)', borderRadius: 10, padding: '0 12px', fontSize: 15, background: 'var(--surface)', width: '100%', minWidth: 0 },
  swapBtn: { width: 44, height: 44, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--teal-800)', cursor: 'pointer' },
  result: { marginTop: 14, padding: '12px 14px', background: 'var(--teal-100)', borderRadius: 8, minHeight: 24 },
  resultMain: { fontSize: 18, fontWeight: 600, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' },
  resultSub: { fontSize: 12.5, color: 'var(--ink-700)', marginTop: 4 },
  source: { fontSize: 12, color: 'var(--ink-500)', marginTop: 10 },
};
